import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { User } from 'lucide-react';
import { t } from '../../lib/i18n';

// Profile keys are snake_case in the DB while i18n labels are camelCase.
const LABEL_KEYS = {
  face_shape: 'faceShape',
  skin_type: 'skinType',
  skin_tone: 'skinTone',
  current_style: 'currentStyle',
  grooming_habits: 'groomingHabits',
  fitness_level: 'fitnessLevel',
};

const humanize = (value) => String(value).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const translateValue = (value, language) => {
  const translated = t(String(value), language);
  return translated === String(value) ? humanize(value) : translated;
};

const formatValue = (value, language) => {
  if (Array.isArray(value)) return value.map(v => translateValue(v, language)).join(', ');
  return translateValue(value, language);
};

const formatLabel = (key, language) => t(LABEL_KEYS[key] || key, language);

export const ProfileCard = ({ user, language }) => {
  const profileEntries = user?.profile && typeof user.profile === 'object'
    ? Object.entries(user.profile).filter(([k, v]) => v != null && v !== '' && k !== 'language').slice(0, 6)
    : [];

  return (
    <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="settings-profile-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <User className="w-5 h-5 text-[var(--lux-gold)]" />
          {t('profile', language)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-black/20 rounded-lg p-3 border border-white/5 min-w-0">
            <span className="text-white/40 text-xs">{t('name', language)}</span>
            <p className="text-white truncate" data-testid="settings-profile-name">{user?.name || '-'}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3 border border-white/5 min-w-0">
            <span className="text-white/40 text-xs">{t('email', language)}</span>
            <p className="text-white truncate" title={user?.email || ''} data-testid="settings-profile-email">{user?.email || '-'}</p>
          </div>
          {profileEntries.map(([key, value]) => (
            <div key={key} className="bg-black/20 rounded-lg p-3 border border-white/5 min-w-0">
              <span className="text-white/40 text-xs">{formatLabel(key, language)}</span>
              <p className="text-white text-sm truncate">{formatValue(value, language)}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
